/**
 * Encrypted Readings Cache using IndexedDB
 * All sensor reading data is encrypted with AES-256-CBC using user-specific keys
 *
 * Cache Strategy:
 * - Historical readings NEVER change once recorded
 * - Track cached time ranges per sensor (not just device)
 * - Support partial cache updates (only fetch missing ranges)
 * - Encrypt all cached data for security
 * - Store sensor metadata with readings
 */

import { encryptionService, EncryptedData } from '../services/encryptionService'
import { Reading } from '../hooks/useDeviceReadings'
import { cacheSettingsService } from '../services/cache/cacheSettingsService'

const DB_NAME = 'ioag-encrypted-readings'
const DB_VERSION = 2  // Incremented for sensor-specific caching
const STORE_NAME = 'encrypted-readings'

/**
 * Time range for cached data
 */
export interface TimeRange {
  from: number  // Unix timestamp in seconds
  to: number    // Unix timestamp in seconds
}

/**
 * Cached readings entry in IndexedDB
 * Key format: `${deviceHash}-${sensorKey}-${userHash}`
 * e.g., "abc123-A1-13-def456" for device "abc123", sensor "A1-13", user "def456"
 */
interface CachedReadingsEntry {
  key: string              // Composite key: `${deviceHash}-${sensorKey}-${userHash}`
  deviceHash: string       // SHA-256 hash of sigfoxId
  sensorKey: string        // Sensor port-category key (e.g., "A1-13")
  userHash: string         // SHA-256 hash of user email for indexing
  timeRanges: TimeRange[]  // List of cached time ranges (sorted, non-overlapping)
  encryptedData: EncryptedData  // AES-256 encrypted readings array
  metadata?: any           // Sensor metadata (productName, sensorName, units)
  lastUpdated: number      // Timestamp of last update
}

/**
 * Decrypted readings data structure
 */
interface ReadingsData {
  cacheKey: string  // Original cache key for reference
  readings: Reading[]
  metadata?: any
}

/**
 * Cache statistics
 */
export interface ReadingsCacheStats {
  totalDevices: number
  totalSensors: number
  totalRanges: number
  totalReadings: number
  oldestTimestamp: number | null
  newestTimestamp: number | null
  cacheSize: number  // Approximate size in bytes
}

/**
 * Encrypted Readings Cache
 * Manages persistent, encrypted storage of sensor readings in IndexedDB
 * Now caches per sensor rather than per device
 */
class EncryptedReadingsCache {
  private db: IDBDatabase | null = null
  private initPromise: Promise<void> | null = null

  /**
   * Initialize IndexedDB connection
   */
  private async init(): Promise<void> {
    if (this.db) return
    if (this.initPromise) return this.initPromise

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => {
        console.error('Failed to open encrypted readings IndexedDB:', request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        console.log('🔒 Encrypted readings cache database initialized')
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        // Clear old store if upgrading from v1
        if (event.oldVersion < 2 && db.objectStoreNames.contains(STORE_NAME)) {
          db.deleteObjectStore(STORE_NAME)
          console.log('🔄 Upgrading cache to v2: sensor-specific storage')
        }

        // Create object store with composite key
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' })
          store.createIndex('deviceHash', 'deviceHash', { unique: false })
          store.createIndex('sensorKey', 'sensorKey', { unique: false })
          store.createIndex('userHash', 'userHash', { unique: false })
          store.createIndex('lastUpdated', 'lastUpdated', { unique: false })
          console.log('🔒 Created encrypted readings store (v2)')
        }
      }
    })

    return this.initPromise
  }

  /**
   * Generate composite key for cache entry
   * Format: `${deviceHash}-${sensorKey}-${userHash}`
   */
  private generateKey(cacheKey: string, email: string): string {
    // cacheKey is already in format `${sigfoxId}-${sensorKey}`
    const keyHash = encryptionService.hashUserIdentifier(cacheKey)
    const userHash = encryptionService.hashUserIdentifier(email)
    return `${keyHash}-${userHash}`
  }

  /**
   * Merge and sort time ranges, combining overlapping/adjacent ranges
   */
  private mergeTimeRanges(ranges: TimeRange[]): TimeRange[] {
    if (ranges.length === 0) return []

    // Sort by start time
    const sorted = [...ranges].sort((a, b) => a.from - b.from)
    const merged: TimeRange[] = [sorted[0]]

    for (let i = 1; i < sorted.length; i++) {
      const current = sorted[i]
      const last = merged[merged.length - 1]

      // If ranges overlap or are adjacent (within 1 second), merge them
      if (current.from <= last.to + 1) {
        last.to = Math.max(last.to, current.to)
      } else {
        merged.push(current)
      }
    }

    return merged
  }

  /**
   * Find gaps in cached time ranges for a requested range
   * Returns array of time ranges that need to be fetched from API
   */
  private findMissingRanges(
    requested: TimeRange,
    cached: TimeRange[]
  ): TimeRange[] {
    if (cached.length === 0) {
      return [requested]
    }

    const missing: TimeRange[] = []
    let currentStart = requested.from

    for (const range of cached) {
      // If there's a gap before this cached range
      if (currentStart < range.from) {
        missing.push({
          from: currentStart,
          to: Math.min(range.from - 1, requested.to)
        })
      }

      // Move current start to after this cached range
      currentStart = Math.max(currentStart, range.to + 1)

      // If we've covered the entire requested range
      if (currentStart > requested.to) {
        break
      }
    }

    // If there's still a gap at the end
    if (currentStart <= requested.to) {
      missing.push({
        from: currentStart,
        to: requested.to
      })
    }

    return missing
  }

  /**
   * Filter readings to only those within a time range
   */
  private filterReadingsByRange(readings: Reading[], range: TimeRange): Reading[] {
    return readings.filter(r => r.timestamp >= range.from && r.timestamp <= range.to)
  }

  /**
   * Get cached readings for a sensor
   * Returns { cached, missing } where cached are the readings we have, and missing are ranges to fetch
   */
  async getCachedReadings(
    cacheKey: string,
    email: string,
    from: number,
    to: number
  ): Promise<{
    readings: Reading[]
    missingRanges: TimeRange[]
    cacheHit: boolean
    metadata?: any
  }> {
    try {
      await this.init()
      if (!this.db) {
        return { readings: [], missingRanges: [{ from, to }], cacheHit: false }
      }

      const key = this.generateKey(cacheKey, email)

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readonly')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.get(key)

        request.onsuccess = () => {
          const result = request.result as CachedReadingsEntry | undefined

          if (!result) {
            // No cache entry - need to fetch everything
            resolve({
              readings: [],
              missingRanges: [{ from, to }],
              cacheHit: false
            })
            return
          }

          try {
            // Decrypt the data (or parse plain JSON if encryption is disabled)
            let decryptedData: ReadingsData

            if (cacheSettingsService.isEncryptionEnabled()) {
              decryptedData = encryptionService.decrypt<ReadingsData>(
                result.encryptedData,
                email
              )
            } else {
              // If encryption is disabled, data is stored as plain JSON in the ciphertext field
              decryptedData = JSON.parse(result.encryptedData.ciphertext)
            }

            // Filter readings to requested time range
            const cachedReadings = this.filterReadingsByRange(
              decryptedData.readings,
              { from, to }
            )

            // Find what ranges are missing
            const missingRanges = this.findMissingRanges(
              { from, to },
              result.timeRanges
            )

            console.log(
              `🔓 Cache ${missingRanges.length === 0 ? 'HIT' : 'PARTIAL'}: ${cachedReadings.length} readings for ${cacheKey}`
            )

            resolve({
              readings: cachedReadings,
              missingRanges,
              cacheHit: missingRanges.length === 0,
              metadata: result.metadata || decryptedData.metadata
            })
          } catch (error) {
            console.error('Failed to decrypt cached readings:', error)
            // If decryption fails, treat as cache miss
            resolve({
              readings: [],
              missingRanges: [{ from, to }],
              cacheHit: false
            })
          }
        }

        request.onerror = () => {
          console.error('Failed to get cached readings:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error accessing encrypted readings cache:', error)
      return { readings: [], missingRanges: [{ from, to }], cacheHit: false }
    }
  }

  /**
   * Cache readings for a sensor
   * Merges with existing cached data and updates time ranges
   */
  async cacheReadings(
    cacheKey: string,
    email: string,
    readings: Reading[],
    timeRange: TimeRange,
    metadata?: any
  ): Promise<void> {
    try {
      await this.init()
      if (!this.db) return

      const key = this.generateKey(cacheKey, email)

      // Extract deviceHash and sensorKey from cacheKey
      // cacheKey format: `${sigfoxId}-${sensorKey}` e.g., "ABC123-A1-13"
      const parts = cacheKey.split('-')
      const sensorKey = parts.length >= 3 ? `${parts[parts.length - 2]}-${parts[parts.length - 1]}` : cacheKey
      const sigfoxId = cacheKey.replace(`-${sensorKey}`, '')
      const deviceHash = encryptionService.hashUserIdentifier(sigfoxId)
      const userHash = encryptionService.hashUserIdentifier(email)

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite')
        const store = transaction.objectStore(STORE_NAME)
        const getRequest = store.get(key)

        getRequest.onsuccess = () => {
          const existing = getRequest.result as CachedReadingsEntry | undefined
          let allReadings: Reading[] = []
          let allRanges: TimeRange[] = [timeRange]
          let existingMetadata = metadata

          if (existing) {
            try {
              // Decrypt existing data (or parse plain JSON if encryption is disabled)
              let existingData: ReadingsData

              if (cacheSettingsService.isEncryptionEnabled()) {
                existingData = encryptionService.decrypt<ReadingsData>(
                  existing.encryptedData,
                  email
                )
              } else {
                existingData = JSON.parse(existing.encryptedData.ciphertext)
              }

              // Merge readings (remove duplicates by timestamp)
              const readingMap = new Map<number, Reading>()

              // Add existing readings
              existingData.readings.forEach(r => {
                readingMap.set(r.timestamp, r)
              })

              // Add new readings (overwrites duplicates)
              readings.forEach(r => {
                readingMap.set(r.timestamp, r)
              })

              // Convert back to array and sort by timestamp
              allReadings = Array.from(readingMap.values()).sort(
                (a, b) => a.timestamp - b.timestamp
              )

              // Merge time ranges
              allRanges = this.mergeTimeRanges([...existing.timeRanges, timeRange])

              // Keep existing metadata if no new metadata provided
              existingMetadata = metadata || existing.metadata
            } catch (error) {
              console.error('Failed to decrypt existing readings, replacing:', error)
              allReadings = readings
            }
          } else {
            allReadings = readings.sort((a, b) => a.timestamp - b.timestamp)
          }

          // Encrypt the merged data (or store as plain JSON if encryption is disabled)
          const dataToEncrypt: ReadingsData = {
            cacheKey,
            readings: allReadings,
            metadata: existingMetadata
          }

          let encryptedData: EncryptedData

          if (cacheSettingsService.isEncryptionEnabled()) {
            encryptedData = encryptionService.encrypt(dataToEncrypt, email)
          } else {
            // Store as plain JSON in ciphertext field when encryption is disabled
            encryptedData = {
              ciphertext: JSON.stringify(dataToEncrypt),
              iv: '',
              salt: ''
            }
          }

          const entry: CachedReadingsEntry = {
            key,
            deviceHash,
            sensorKey,
            userHash,
            timeRanges: allRanges,
            encryptedData,
            metadata: existingMetadata,
            lastUpdated: Date.now()
          }

          const putRequest = store.put(entry)

          putRequest.onsuccess = () => {
            console.log(
              `🔐 Cached ${readings.length} readings for ${cacheKey} (total: ${allReadings.length}, ranges: ${allRanges.length})`
            )
            resolve()
          }

          putRequest.onerror = () => {
            console.error('Failed to cache readings:', putRequest.error)
            reject(putRequest.error)
          }
        }

        getRequest.onerror = () => {
          console.error('Failed to get existing cache entry:', getRequest.error)
          reject(getRequest.error)
        }
      })
    } catch (error) {
      console.error('Error caching encrypted readings:', error)
    }
  }

  /**
   * Get cached time ranges for a sensor
   */
  async getCachedTimeRanges(
    cacheKey: string,
    email: string
  ): Promise<TimeRange[]> {
    try {
      await this.init()
      if (!this.db) return []

      const key = this.generateKey(cacheKey, email)

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readonly')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.get(key)

        request.onsuccess = () => {
          const result = request.result as CachedReadingsEntry | undefined
          resolve(result?.timeRanges || [])
        }

        request.onerror = () => {
          console.error('Failed to get cached time ranges:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error getting cached time ranges:', error)
      return []
    }
  }

  /**
   * Get all sensor keys for a device
   * Useful for discovering what sensors exist without an API call
   */
  async getDeviceSensors(
    sigfoxId: string,
    email: string
  ): Promise<string[]> {
    try {
      await this.init()
      if (!this.db) return []

      const deviceHash = encryptionService.hashUserIdentifier(sigfoxId)

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readonly')
        const store = transaction.objectStore(STORE_NAME)
        const index = store.index('deviceHash')
        const request = index.openCursor(IDBKeyRange.only(deviceHash))

        const sensorKeys: string[] = []

        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result
          if (cursor) {
            const entry = cursor.value as CachedReadingsEntry
            sensorKeys.push(entry.sensorKey)
            cursor.continue()
          } else {
            console.log(`📋 Found ${sensorKeys.length} cached sensors for device ${sigfoxId}`)
            resolve(sensorKeys)
          }
        }

        request.onerror = () => {
          console.error('Failed to get device sensors:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error getting device sensors:', error)
      return []
    }
  }

  /**
   * Clear cached readings for a specific sensor
   */
  async clearSensor(cacheKey: string, email: string): Promise<void> {
    try {
      await this.init()
      if (!this.db) return

      const key = this.generateKey(cacheKey, email)

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.delete(key)

        request.onsuccess = () => {
          console.log(`🗑️ Cleared readings cache for sensor ${cacheKey}`)
          resolve()
        }

        request.onerror = () => {
          console.error('Failed to clear sensor readings cache:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error clearing sensor readings cache:', error)
    }
  }

  /**
   * Clear cached readings for a specific device (all sensors)
   */
  async clearDevice(sigfoxId: string, email: string): Promise<void> {
    try {
      await this.init()
      if (!this.db) return

      const deviceHash = encryptionService.hashUserIdentifier(sigfoxId)

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite')
        const store = transaction.objectStore(STORE_NAME)
        const index = store.index('deviceHash')
        const request = index.openCursor(IDBKeyRange.only(deviceHash))

        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result
          if (cursor) {
            cursor.delete()
            cursor.continue()
          } else {
            console.log(`🗑️ Cleared readings cache for device ${sigfoxId}`)
            resolve()
          }
        }

        request.onerror = () => {
          console.error('Failed to clear device readings cache:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error clearing device readings cache:', error)
    }
  }

  /**
   * Clear all cached readings for a user
   */
  async clearUserCache(email: string): Promise<void> {
    try {
      await this.init()
      if (!this.db) return

      const userHash = encryptionService.hashUserIdentifier(email)

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite')
        const store = transaction.objectStore(STORE_NAME)
        const index = store.index('userHash')
        const request = index.openCursor(IDBKeyRange.only(userHash))

        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result
          if (cursor) {
            cursor.delete()
            cursor.continue()
          } else {
            console.log(`🗑️ Cleared all readings cache for user`)
            resolve()
          }
        }

        request.onerror = () => {
          console.error('Failed to clear user readings cache:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error clearing user readings cache:', error)
    }
  }

  /**
   * Clear all cached readings
   */
  async clearAll(): Promise<void> {
    try {
      await this.init()
      if (!this.db) return

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.clear()

        request.onsuccess = () => {
          console.log('🗑️ Cleared all readings cache')
          resolve()
        }

        request.onerror = () => {
          console.error('Failed to clear all readings cache:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error clearing all readings cache:', error)
    }
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(email?: string): Promise<ReadingsCacheStats> {
    try {
      await this.init()
      if (!this.db) {
        return this.getEmptyStats()
      }

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readonly')
        const store = transaction.objectStore(STORE_NAME)

        let request: IDBRequest
        if (email) {
          const userHash = encryptionService.hashUserIdentifier(email)
          const index = store.index('userHash')
          request = index.getAll(IDBKeyRange.only(userHash))
        } else {
          request = store.getAll()
        }

        request.onsuccess = () => {
          const entries = request.result as CachedReadingsEntry[]

          const deviceSet = new Set<string>()
          const sensorSet = new Set<string>()
          let totalRanges = 0
          let totalReadings = 0
          let oldestTimestamp: number | null = null
          let newestTimestamp: number | null = null
          let cacheSize = 0

          for (const entry of entries) {
            deviceSet.add(entry.deviceHash)
            sensorSet.add(entry.sensorKey)
            totalRanges += entry.timeRanges.length

            // Estimate cache size
            cacheSize += JSON.stringify(entry.encryptedData).length

            // Update oldest/newest timestamps from ranges
            for (const range of entry.timeRanges) {
              if (oldestTimestamp === null || range.from < oldestTimestamp) {
                oldestTimestamp = range.from
              }
              if (newestTimestamp === null || range.to > newestTimestamp) {
                newestTimestamp = range.to
              }
            }

            // Try to decrypt to get reading count (if email provided)
            if (email) {
              try {
                let data: ReadingsData

                if (cacheSettingsService.isEncryptionEnabled()) {
                  data = encryptionService.decrypt<ReadingsData>(
                    entry.encryptedData,
                    email
                  )
                } else {
                  data = JSON.parse(entry.encryptedData.ciphertext)
                }
                totalReadings += data.readings.length
              } catch {
                // Skip if decryption fails
              }
            }
          }

          const stats: ReadingsCacheStats = {
            totalDevices: deviceSet.size,
            totalSensors: sensorSet.size,
            totalRanges,
            totalReadings,
            oldestTimestamp,
            newestTimestamp,
            cacheSize
          }

          console.log(`📊 Readings cache stats:`, stats)
          resolve(stats)
        }

        request.onerror = () => {
          console.error('Failed to get cache stats:', request.error)
          resolve(this.getEmptyStats())
        }
      })
    } catch (error) {
      console.error('Error getting readings cache stats:', error)
      return this.getEmptyStats()
    }
  }

  private getEmptyStats(): ReadingsCacheStats {
    return {
      totalDevices: 0,
      totalSensors: 0,
      totalRanges: 0,
      totalReadings: 0,
      oldestTimestamp: null,
      newestTimestamp: null,
      cacheSize: 0
    }
  }
}

// Export singleton instance
export const encryptedReadingsCache = new EncryptedReadingsCache()
export type { CachedReadingsEntry, ReadingsData }
